import { tutorialAssets } from "assets/cdn";
import { useImagePath } from "hooks/useImagePath";
import { Grid, useMediaQuery } from "@mui/material";
import Button from "components/Buttons/ThemeButton";
import useCalculateScale from "components/PopUpManager/hooks/useCalculateScale";
import { useWebStoreContext } from "contexts";
import { IShopLoyaltyOffer } from "contexts/WebStoreProvider/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { offersApi } from "redux/apis";
import { Arrow, Background2, Shine, Text, Tiki2 } from "../../styles";
import {
	ArrowContainer,
	Container,
	SlideNumberContainer,
	StyledText
} from "./style";

interface Slide2Props {
	onNext: () => void;
	onSkip: () => void;
	slideNumber: number;
}

export const Slide2 = ({ onNext, onSkip, slideNumber }: Slide2Props) => {
	const { getImage } = useImagePath("cdn");
	const background = getImage(tutorialAssets.background);
	const arrowUp = getImage(tutorialAssets.greenArrow);
	const shineAsset = getImage(tutorialAssets.shine);
	const tiki = getImage(tutorialAssets.char2);
	const isLowHeight = useMediaQuery("(max-height: 599px)");
	//const isExtraSmall = useMediaQuery("(max-width: 380px)");
	const isPortrait = useMediaQuery("(orientation: portrait)");
	const scale = useCalculateScale(
		isPortrait ? 380 : 750,
		isPortrait ? 200 : 250
	);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const repositionFrameRef = useRef<number | null>(null);
	const { authToken, store } = useWebStoreContext();
	const [firstOffer, setFirstOffer] = useState<HTMLElement | null>(null);
	const [tutorialPosition, setTutorialPosition] = useState<{
		top: number;
		left: number;
	}>({ top: 0, left: 0 });
	const [isFirstScroll, setIsFirstScroll] = useState<boolean>(true);

	// Refs for values that change without recreating listeners
	const isLowHeightRef = useRef(isLowHeight);
	const isPortraitRef = useRef(isPortrait);

	useEffect(() => {
		isLowHeightRef.current = isLowHeight;
		isPortraitRef.current = isPortrait;
	}, [isLowHeight, isPortrait]);

	const { data: loyaltyOffers }: { data?: { offers?: IShopLoyaltyOffer[] } } =
		offersApi.endpoints.getOffers.useQueryState({
			offerType: "loyalty",
			isAuthenticated: !!authToken?.value
		});

	const sectionOffers = useMemo(() => {
		return store?.loyaltyShopSections
			.filter((data) =>
				loyaltyOffers?.offers?.find(
					(of: IShopLoyaltyOffer) => of.sectionName === data.name
				)
			)
			.map((data) => {
				const sectionOffers: IShopLoyaltyOffer[] | undefined =
					loyaltyOffers?.offers?.filter(
						(of: IShopLoyaltyOffer) => of.sectionName === data.name
					);
				return sectionOffers;
			});
	}, [loyaltyOffers, store?.loyaltyShopSections]);

	const centerElementWithScroll = (element: HTMLElement) => {
		return new Promise((resolve) => {
			const rect = element.getBoundingClientRect();
			const scrollTop = rect.top + window.scrollY - 65;
			!isLowHeightRef?.current || isPortraitRef?.current
				? window.scrollTo({
						top: scrollTop,
						behavior: isFirstScroll ? "smooth" : "instant"
				  })
				: element.scrollIntoView({
						block: "center",
						behavior: isFirstScroll ? "smooth" : "instant"
				  });

			resolve(setIsFirstScroll(false));
		});
	};

	const scrollAndHighlight = async () => {
		if (firstOffer) {
			centerElementWithScroll(firstOffer).then(() => {
				firstOffer.setAttribute("style", "z-index: 99999;");

				const firstOfferPosition = firstOffer.getBoundingClientRect();
				const rect = containerRef?.current?.getBoundingClientRect();
				if (firstOfferPosition && containerRef?.current && rect) {
					setTutorialPosition({
						top:
							isLowHeightRef?.current && !isPortraitRef?.current
								? firstOfferPosition.top +
								  firstOfferPosition.height / 2 -
								  containerRef?.current?.offsetHeight / 2
								: firstOfferPosition.top + firstOfferPosition.height + 10,
						left:
							isLowHeightRef?.current && !isPortraitRef?.current
								? firstOfferPosition.right + 10
								: firstOfferPosition.left +
								  firstOfferPosition.width / 2 -
								  rect.width / 4.5
					});
				}
			});
		}
	};

	useEffect(() => {
		if (sectionOffers) {
			const firstSingleItems = sectionOffers.find((so) =>
				so?.find((o) => o.sectionName !== "daily_deals")
			);
			const firstOffer = document.getElementById(
				firstSingleItems?.[0].id as string
			);
			setFirstOffer(firstOffer);
		}
	}, [sectionOffers]);

	useEffect(() => {
		if (isFirstScroll) {
			scrollAndHighlight();
		}

		return () => {
			firstOffer?.removeAttribute("style");
		};
	}, [firstOffer]);

	const scheduleReposition = () => {
		if (repositionFrameRef.current !== null) {
			cancelAnimationFrame(repositionFrameRef.current);
		}

		repositionFrameRef.current = requestAnimationFrame(() => {
			repositionFrameRef.current = requestAnimationFrame(() => {
				scrollAndHighlight();
				repositionFrameRef.current = null;
			});
		});
	};

	const leaveSlide = (callback: () => void) => {
		if (repositionFrameRef.current !== null) {
			cancelAnimationFrame(repositionFrameRef.current);
			repositionFrameRef.current = null;
		}
		firstOffer?.removeAttribute("style");
		callback();
	};

	useEffect(() => {
		const viewport = window.visualViewport;

		window.addEventListener("resize", scheduleReposition);
		window.addEventListener("scrollend", scheduleReposition);
		window.addEventListener("transitionend", scheduleReposition);
		viewport?.addEventListener("resize", scheduleReposition);

		scheduleReposition();

		return () => {
			window.removeEventListener("resize", scheduleReposition);
			window.removeEventListener("scrollend", scheduleReposition);
			window.removeEventListener("transitionend", scheduleReposition);
			viewport?.removeEventListener("resize", scheduleReposition);

			if (repositionFrameRef.current !== null) {
				cancelAnimationFrame(repositionFrameRef.current);
				repositionFrameRef.current = null;
			}
		};
	}, [firstOffer, isPortrait, isLowHeight, scale, isFirstScroll]);

	return (
		<>
			{!isFirstScroll && (
				<Container
					ref={containerRef}
					scale={scale}
					left={tutorialPosition.left}
					top={tutorialPosition.top}
					hasScrollEnded={!isFirstScroll}
				>
					<ArrowContainer>
						<Arrow
							className={isLowHeight && !isPortrait ? "side-to-side-left" : ""}
							src={arrowUp}
							alt="Arrow up"
							width={46}
						/>
					</ArrowContainer>
					<div>
						<SlideNumberContainer>
							<Text
								color="#fff"
								fontFamily="Baloo"
								fontSize="16px"
								lineHeight="17px"
							>
								2/{slideNumber}
							</Text>
						</SlideNumberContainer>

						<Grid item width={331}>
							<Background2 $backgroundImage={background}>
								<div>
									<Tiki2 src={tiki} alt="Tiki" bottom="10px" />
								</div>
								<div>
									<StyledText style={{ paintOrder: "stroke fill" }}>
										<span>Redeem</span> LP for Wild Cards,
										<br /> Volcano Cards, Coins and
										<br /> Boosters.
									</StyledText>
									<Shine
										src={shineAsset}
										width={40}
										height={40}
										className="animate-on-text"
									/>
								</div>
								<Button className="next" onClick={() => leaveSlide(onNext)}>
									Next
								</Button>
								<Button
									className="skip"
									variant="text"
									onClick={() => leaveSlide(onSkip)}
								>
									Skip
								</Button>
							</Background2>
						</Grid>
					</div>
				</Container>
			)}
		</>
	);
};
